<template>
	<div v-if="toastNotifications.length > 0 && currentToast" class="toast-container">
		<v-card
			class="toast-card"
			:class="{
				'new-notification': showNewNotificationAnimation,
				collapsed: isCollapsed,
				'has-carousel': !isCollapsed,
			}"
			:style="{ '--notification-color': toastColor, '--contrast-color': contrastColor }"
			rounded="lg"
			elevation="4"
			:ripple="false"
			v-bind="isCollapsed ? { onClick: handleToastClick } : {}"
			@mouseenter="handleMouseEnter"
			@mouseleave="handleMouseLeave"
		>
			<v-alert :color="toastColor" :title="currentToast.label" density="compact" rounded="lg" variant="flat">
				<template #title>
					<div class="d-flex align-center justify-space-between" style="width: 100%">
						<v-icon class="ma-1" :icon="toastIcon" size="small"></v-icon>
						<span class="toast-title text-truncate">{{ currentToast.label }}</span>
						<div class="d-flex align-center">
							<v-btn v-if="isCollapsed" variant="text" size="default" icon="mdi-chevron-down" />
							<v-tooltip
								v-if="!isCollapsed"
								:text="
									isPinned
										? symbolsStore.GUILabel('6bd98cad-79e1-4a5b-9962-a449df626e5b')
										: symbolsStore.GUILabel('ba46dc1f-bed3-450a-b9b0-fdb8a3794d80')
								"
								open-delay="500"
								location="bottom"
							>
								<template #activator="{ props }">
									<v-btn
										class="pin-btn"
										:class="{ 'pin-btn--active': isPinned }"
										variant="text"
										size="default"
										:icon="isPinned ? 'mdi-pin' : 'mdi-pin-outline'"
										v-bind="props"
										@click.stop="togglePin"
									/>
								</template>
							</v-tooltip>
							<v-btn
								v-if="!isCollapsed"
								variant="text"
								size="default"
								icon="mdi-chevron-up"
								@click.stop="collapseToast"
							/>
						</div>
					</div>
				</template>

				<template #text>
					<div class="toast-content" :class="{ 'collapsed-content': isCollapsed }">
						<div>
							{{ currentToast.description }}
						</div>
						<div class="toast-counter">
							<span v-if="pendingNotificationQueue.length > 0" class="pending-badge">
								+{{ pendingNotificationQueue.length }}
							</span>
							<span :style="{ visibility: toastNotifications.length > 1 ? 'visible' : 'hidden' }">
								{{ currentIndex + 1 }}/{{ toastNotifications.length }}
							</span>
						</div>
					</div>
				</template>
			</v-alert>

			<!-- Progress bar for collapse timer -->
			<div
				v-if="!isCollapsed && (!isPinned || pendingNotificationQueue.length > 0)"
				class="collapse-progress-bar"
				:style="{
					'--progress-color': toastColor,
					'--progress-width': progressWidth + '%',
				}"
			></div>
			<!-- Carousel navigation zones -->
			<div
				v-if="!isCollapsed"
				class="carousel-zone carousel-zone--left"
				:class="{ 'carousel-zone--disabled': toastNotifications.length <= 1 || currentIndex === 0 }"
				@click.stop="previousToast"
			>
				<v-icon size="32" icon="mdi-chevron-left" />
			</div>
			<div
				v-if="!isCollapsed"
				class="carousel-zone carousel-zone--right"
				:class="{
					'carousel-zone--disabled':
						toastNotifications.length <= 1 || currentIndex === toastNotifications.length - 1,
				}"
				@click.stop="nextToast"
			>
				<v-icon size="32" icon="mdi-chevron-right" />
			</div>
		</v-card>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect, onMounted, onUnmounted } from "vue";
import { useGraphStore } from "../../../store/graph";
import { useSymbolsStore } from "../../../store/symbols";

const graphStore = useGraphStore();
const symbolsStore = useSymbolsStore();

const DEFAULT_TOAST_COLOR = "#4db6ac";
const DISPLAY_DURATION = 6_000;
const MIN_DISPLAY_DURATION = 3_000;
const PROGRESS_INTERVAL = 50;
const ANIMATION_DURATION = 750;

const currentIndex = ref(0);
const isCollapsed = ref(false);
const isPinned = ref(true);
const isHovered = ref(false);
const showNewNotificationAnimation = ref(false);
const pendingNotificationQueue = ref<string[]>([]);
const progressWidth = ref(100);

let displayTimer: NodeJS.Timeout | null = null;
let progressTimer: NodeJS.Timeout | null = null;
let displayedAt = Date.now();

const toastNotifications = ref<any[]>([]);

// graphStore.descriptor.notifications is mutated externally and re-sorted on every change.
// We adjust currentIndex atomically with the array swap so the displayed notification
// never briefly points at a different item between reactive updates.
watchEffect(() => {
	const newToasts = graphStore.descriptor.notifications
		.filter((n) => n.display === "toast")
		.sort((a, b) => b.timestamp - a.timestamp);

	const currentIds = toastNotifications.value.map((n) => n.id).join(",");
	const newIds = newToasts.map((n) => n.id).join(",");

	if (currentIds !== newIds) {
		const currentToastId = toastNotifications.value[currentIndex.value]?.id;
		toastNotifications.value = newToasts;
		if (currentToastId) {
			const idx = newToasts.findIndex((n) => n.id === currentToastId);
			if (idx !== -1) currentIndex.value = idx;
		}
		if (currentIndex.value >= newToasts.length) {
			currentIndex.value = 0;
		}
	}
});

const currentToast = computed(() => {
	if (toastNotifications.value.length === 0) return null;
	return toastNotifications.value[currentIndex.value];
});

const toastColor = computed(() => currentToast.value?.color || DEFAULT_TOAST_COLOR);

function getLuminance(hexColor: string): number {
	const hex = hexColor.replace("#", "");
	let r = parseInt(hex.substring(0, 2), 16) / 255;
	let g = parseInt(hex.substring(2, 4), 16) / 255;
	let b = parseInt(hex.substring(4, 6), 16) / 255;
	r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
	g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
	b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const contrastColor = computed(() => {
	const luminance = getLuminance(toastColor.value);
	return luminance > 0.4 ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.9)";
});

const toastIcon = computed(() => {
	if (!currentToast.value) return "";
	const icon = currentToast.value.icon || "lightbulb-on";
	return icon.startsWith("mdi-") ? icon : `mdi-${icon}`;
});

// --- Unified display timer ---

function clearTimers() {
	if (displayTimer) {
		clearTimeout(displayTimer);
		displayTimer = null;
	}
	if (progressTimer) {
		clearInterval(progressTimer);
		progressTimer = null;
	}
	progressWidth.value = 100;
}

function startDisplayTimer(duration: number = DISPLAY_DURATION) {
	clearTimers();

	if (isPinned.value && pendingNotificationQueue.value.length === 0) return;

	const startedAt = Date.now();

	progressTimer = setInterval(() => {
		if (isHovered.value) return;
		const remaining = Math.max(0, duration - (Date.now() - startedAt));
		progressWidth.value = (remaining / duration) * 100;
		if (remaining <= 0 && progressTimer) {
			clearInterval(progressTimer);
			progressTimer = null;
		}
	}, PROGRESS_INTERVAL);

	displayTimer = setTimeout(() => {
		displayTimer = null;
		if (isHovered.value) return;

		if (pendingNotificationQueue.value.length > 0) {
			showNextPending();
		} else if (!isPinned.value) {
			isCollapsed.value = true;
			clearTimers();
		} else {
			clearTimers();
		}
	}, duration);
}

function showNextPending() {
	while (pendingNotificationQueue.value.length > 0) {
		const nextId = pendingNotificationQueue.value.shift()!;
		const idx = toastNotifications.value.findIndex((n) => n.id === nextId);
		if (idx !== -1) {
			currentIndex.value = idx;
			displayedAt = Date.now();
			triggerNewAnimation();
			startDisplayTimer();
			return;
		}
	}
	if (!isPinned.value) {
		isCollapsed.value = true;
	}
	clearTimers();
}

function triggerNewAnimation() {
	showNewNotificationAnimation.value = false;
	requestAnimationFrame(() => {
		showNewNotificationAnimation.value = true;
		setTimeout(() => {
			showNewNotificationAnimation.value = false;
		}, ANIMATION_DURATION);
	});
}

// --- Notification arrival ---

watch(
	toastNotifications,
	(newToasts, oldToasts) => {
		const oldIds = new Set(oldToasts?.map((n) => n.id) || []);
		const added = newToasts.filter((n) => !oldIds.has(n.id));

		if (added.length > 0) {
			const wasEmpty = !oldToasts || oldToasts.length === 0;

			if (wasEmpty || isCollapsed.value) {
				currentIndex.value = 0;
				displayedAt = Date.now();
				isCollapsed.value = false;
				triggerNewAnimation();
				startDisplayTimer();
			} else {
				const sortedAdded = [...added].sort((a, b) => b.timestamp - a.timestamp);
				pendingNotificationQueue.value.push(...sortedAdded.map((n) => n.id));

				if (!displayTimer) {
					const elapsed = Date.now() - displayedAt;
					const remaining = DISPLAY_DURATION - elapsed;
					if (remaining <= 0) {
						showNextPending();
					} else {
						startDisplayTimer(Math.max(MIN_DISPLAY_DURATION, remaining));
					}
				}
			}
		}

		if (pendingNotificationQueue.value.length > 0) {
			const ids = new Set(newToasts.map((n) => n.id));
			pendingNotificationQueue.value = pendingNotificationQueue.value.filter((id) => ids.has(id));
		}

		if (currentIndex.value >= newToasts.length) {
			currentIndex.value = 0;
		}
	},
	{ immediate: true },
);

// --- User interactions ---

function handleMouseEnter() {
	isHovered.value = true;
	clearTimers();
}

function handleMouseLeave() {
	isHovered.value = false;
	if (!isCollapsed.value) {
		startDisplayTimer();
	}
}

function handleToastClick() {
	if (isCollapsed.value) {
		isCollapsed.value = false;
		startDisplayTimer();
	}
}

function collapseToast() {
	isCollapsed.value = true;
	isPinned.value = false;
	pendingNotificationQueue.value = [];
	clearTimers();
}

function nextToast() {
	currentIndex.value = currentIndex.value < toastNotifications.value.length - 1 ? currentIndex.value + 1 : 0;
}

function previousToast() {
	currentIndex.value = currentIndex.value > 0 ? currentIndex.value - 1 : toastNotifications.value.length - 1;
}

function togglePin() {
	isPinned.value = !isPinned.value;
	if (isPinned.value) {
		clearTimers();
		if (pendingNotificationQueue.value.length > 0) {
			startDisplayTimer();
		}
	} else {
		startDisplayTimer();
	}
}

onMounted(() => {
	if (toastNotifications.value.length > 0) {
		startDisplayTimer();
	}
});

onUnmounted(() => {
	clearTimers();
});
</script>

<style scoped lang="scss">
.toast-container {
	position: absolute;
	top: 58px;
	left: calc(100% - var(--questions-sidebar-width) - 515px);
	z-index: 999;
	width: 500px;
	max-width: 500px;
	pointer-events: none;
	display: flex;
	justify-content: flex-end;
}

.toast-card {
	background-color: rgba(var(--v-theme-surface), 0.95);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	position: relative;
	pointer-events: auto;
	transition: all 0.3s ease;
	cursor: default;
	width: 100%;
	max-width: 500px;

	/* Prevent Vuetify hover overlay from lightening dark colors */
	:deep(.v-alert__underlay),
	:deep(.v-alert .v-alert__overlay) {
		display: none !important;
	}

	/* Reduce horizontal padding on the alert */
	.v-alert {
		padding-left: 12px !important;
		padding-right: 12px !important;
	}

	/* When carousel zones are visible, add padding so text avoids them */
	&.has-carousel {
		.v-alert {
			padding-left: 68px !important;
			padding-right: 68px !important;
		}
	}

	/* Disable Vuetify card hover effects when not collapsed */
	&:not(.collapsed) {
		&:hover,
		&:focus,
		&:active {
			transform: none !important;
			filter: none !important;
		}

		/* Enable text selection for content */
		.toast-title,
		.toast-content,
		.v-alert__text {
			user-select: text;
			cursor: text;
		}
	}

	&.collapsed {
		cursor: pointer;
		opacity: 0.7;
		width: 200px;
		transform-origin: top right;

		.v-alert {
			padding-bottom: 8px !important;
		}

		&:hover {
			opacity: 1;
		}

		/* Disable text selection when collapsed */
		.toast-title,
		.toast-content,
		.v-alert__text {
			user-select: none;
		}
	}

	&:hover:not(.collapsed) {
		.collapse-progress-bar {
			opacity: 0;
			transition: none;
		}

		/* Prevent any hover effects on the alert - ensure background stays consistent */
		:deep(.v-alert) {
			background-color: var(--notification-color) !important;
			border-color: var(--notification-color) !important;
		}
	}

	&.new-notification {
		animation: pulseGlow 0.75s ease-out;

		&::before {
			content: "";
			position: absolute;
			top: -3px;
			left: -3px;
			right: -3px;
			bottom: -3px;
			background: linear-gradient(
				45deg,
				var(--notification-color),
				color-mix(in srgb, var(--notification-color) 80%, white)
			);
			border-radius: inherit;
			z-index: -1;
			opacity: 0;
			animation: glowBorder 0.75s ease-out;
		}
	}
}

.collapse-progress-bar {
	position: absolute;
	bottom: 0;
	left: 0;
	height: 3px;
	width: var(--progress-width);
	background: var(--contrast-color);
	border-radius: 0 0 0 0;
	transition: width 0.05s linear, opacity 0.3s ease;
	z-index: 1;
	opacity: 0.8;
}

.toast-content {
	overflow: hidden;

	&.collapsed-content {
		max-height: 0;
		opacity: 0;
		margin: 0 !important;
		padding: 0 !important;
		transition: all 0.3s ease;
	}
}

@keyframes pulseGlow {
	0% {
		transform: scale(1);
		filter: brightness(1);
	}
	50% {
		transform: scale(1.02);
		filter: brightness(1.1);
	}
	100% {
		transform: scale(1);
		filter: brightness(1);
	}
}

@keyframes glowBorder {
	0% {
		opacity: 0;
		transform: scale(0.95);
	}
	50% {
		opacity: 0.8;
		transform: scale(1.05);
	}
	100% {
		opacity: 0;
		transform: scale(1.1);
	}
}
.toast-title {
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.pin-btn {
	&--active {
		:deep(.v-icon) {
			color: var(--contrast-color) !important;
			opacity: 1;
		}
	}
}

.carousel-zone {
	position: absolute;
	top: 0;
	bottom: 0;
	width: 60px;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: var(--contrast-color);
	opacity: 1;
	transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);

	// MD ripple-like overlay on hover
	&::before {
		content: "";
		position: absolute;
		inset: 4px 0;
		background: currentColor;
		opacity: 0;
		border-radius: 6px;
		transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		pointer-events: none;
	}

	&:hover {
		opacity: 1;

		&::before {
			opacity: 0.08;
		}
	}

	&:active {
		opacity: 1;

		&::before {
			opacity: 0.16;
		}

		.v-icon {
			transform: scale(0.85);
		}
	}

	.v-icon {
		transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
	}

	&--left {
		left: 0;
		border-radius: 8px 0 0 8px;
	}

	&--right {
		right: 0;
		border-radius: 0 8px 8px 0;
	}

	&--disabled {
		opacity: 0.15;
		pointer-events: none;
	}
}

.toast-counter {
	text-align: right;
	color: var(--contrast-color);
	opacity: 0.8;
	font-size: 1rem !important;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 6px;
}

.pending-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: var(--contrast-color);
	color: var(--notification-color);
	font-size: 0.7rem;
	font-weight: 700;
	line-height: 1;
	padding: 2px 5px;
	border-radius: 8px;
	min-width: 20px;
	animation: badgePulse 1.5s ease-in-out infinite;
}

@keyframes badgePulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
}
</style>
